/**
 * Compute each member's shareholding-% from the live `shares_held` values
 * on the company.members array. Denominator is the total shares HELD across
 * all members of the same class (e.g. Equity). When a member has 0 shares
 * the pct is 0; when total is 0 (uninitialised company) every member is 0.
 *
 * This intentionally ignores the stored `shareholding_percent` field because
 * it routinely goes stale (manual entry, not recomputed on transfer, etc.).
 *
 * Returns the same `members` array with `shareholding_percent` filled in.
 * Caller can spread the result back into the company object.
 */
export function computeShareholdingPercents(members) {
  if (!Array.isArray(members) || members.length === 0) return members || [];

  // Compute per-class totals.
  const totalsByClass = {};
  for (const m of members) {
    const cls = (m.member_type || "Equity").trim() || "Equity";
    const s = Number(m.shares_held) || 0;
    totalsByClass[cls] = (totalsByClass[cls] || 0) + s;
  }

  return members.map((m) => {
    const cls = (m.member_type || "Equity").trim() || "Equity";
    const total = totalsByClass[cls] || 0;
    const held = Number(m.shares_held) || 0;
    const pct = total > 0 ? +((held / total) * 100).toFixed(2) : 0;
    return { ...m, shareholding_percent: pct };
  });
}

/** Convenience — get a single member's pct without rewriting the whole list. */
export function memberSharePct(members, member) {
  const enriched = computeShareholdingPercents(members);
  const match = enriched.find(
    (m) => m.folio_no === member.folio_no || m.name === member.name,
  );
  return match?.shareholding_percent ?? 0;
}
